"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.googleAdsGetExtensionDetails = exports.googleAdsGetExtensionDetailsResultSchema = exports.googleAdsGetExtensionDetailsArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
var googleAdsGetExtensionDetailsInputSchema = zod_1.z
    .object({
    customerId: zod_1.z
        .string()
        .nullish()
        .describe('ONLY provide if user explicitly specifies a Google Ads customer ID. DO NOT generate or guess customer IDs. Leave empty to automatically use the selected account. Must be provided by user - never invent random numbers.'),
    campaignId: zod_1.z
        .string()
        .nullish()
        .describe('Specific campaign ID to analyze. If not provided, returns data for all campaigns in the account.'),
    extensionType: zod_1.z
        .enum(['SITELINK', 'CALLOUT', 'STRUCTURED_SNIPPET', 'ALL'])
        .nullish()
        .default('ALL')
        .describe('Filter by extension type. Defaults to ALL.'),
    startDate: zod_1.z
        .string()
        .nullish()
        .describe('Start date for performance data in YYYY-MM-DD format. e.g. 2025-08-01. Optional.'),
    endDate: zod_1.z
        .string()
        .nullish()
        .describe('End date for performance data in YYYY-MM-DD format. e.g. 2025-08-31. Optional.'),
    metrics: zod_1.z
        .array(zod_1.z.enum(['impressions', 'clicks', 'cost', 'conversions', 'ctr', 'cpc', 'cpm']))
        .nullish()
        .default(['impressions', 'clicks', 'cost', 'ctr'])
        .describe('Metrics to include in the response. Defaults to basic metrics.'),
    maxItems: zod_1.z
        .number()
        .min(500)
        .default(500)
        .describe('The maximum number of extensions to fetch. Unless specified, defaults to 500. Minimum is 500.'),
    includeDisabled: zod_1.z
        .boolean()
        .default(false)
        .describe('Include paused, removed, or disabled extensions in results. Set to true to show all extensions regardless of status. Defaults to false.'),
    options: zod_1.z
        .object({
        failIfEmpty: zod_1.z
            .boolean()
            .default(true)
            .describe('Fail if no extension data is found. Unless specified, defaults to true.'),
        groupByDay: zod_1.z
            .boolean()
            .default(false)
            .describe('Group performance data by day. Unless specified, defaults to false.'),
    })
        .describe('Performance query options'),
})
    .describe('Get performance metrics for ad extensions (sitelinks, callouts, structured snippets) in Google Ads campaigns. Shows which extensions drive clicks and conversions.');
exports.googleAdsGetExtensionDetailsArgsSchema = (0, common_1.GoogleAdsNodeArgsSchema)(googleAdsGetExtensionDetailsInputSchema);
var googleAdsGetExtensionDetailsResponseSchema = zod_1.z.array(zod_1.z.object({
    extensionId: zod_1.z.string().describe('The extension ID'),
    extensionName: zod_1.z.string().nullish().describe('The extension text or name'),
    extensionType: zod_1.z
        .enum(['SITELINK', 'CALLOUT', 'STRUCTURED_SNIPPET'])
        .describe('The type of extension'),
    campaignId: zod_1.z.string().nullish().describe('The campaign ID'),
    campaignName: zod_1.z.string().nullish().describe('The campaign name'),
    status: zod_1.z.string().nullish().describe('Extension status'),
    performance: zod_1.z
        .object({
        impressions: zod_1.z.number().nullish(),
        clicks: zod_1.z.number().nullish(),
        cost: zod_1.z.number().nullish(),
        conversions: zod_1.z.number().nullish(),
        ctr: zod_1.z.number().nullish(),
        cpc: zod_1.z.number().nullish(),
        cpm: zod_1.z.number().nullish(),
        conversionRate: zod_1.z.number().nullish(),
    })
        .describe('Performance metrics for the extension'),
    dailyBreakdown: zod_1.z
        .array(zod_1.z.object({
        date: zod_1.z.string(),
        impressions: zod_1.z.number().nullish(),
        clicks: zod_1.z.number().nullish(),
        cost: zod_1.z.number().nullish(),
    }))
        .nullish()
        .describe('Daily performance breakdown if groupByDay is enabled'),
}));
exports.googleAdsGetExtensionDetailsResultSchema = googleAdsGetExtensionDetailsResponseSchema;
exports.googleAdsGetExtensionDetails = (0, common_1.createEmitModeToolMetadata)({
    id: 'googleAdsGetExtensionDetails',
    displayName: 'Google Ads Extension Performance',
    displayDescription: 'Get performance metrics for ad extensions (sitelinks, callouts, structured snippets) in Google Ads campaigns. Shows which extensions drive clicks and conversions. Automatically uses the selected account unless user provides specific customer ID.',
    category: 'ads',
    color: common_1.BRAND_COLORS.GOOGLE_ADS,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.GOOGLE_ADS),
    icon: 'google_ads',
    argsSchema: exports.googleAdsGetExtensionDetailsArgsSchema,
}, googleAdsGetExtensionDetailsResponseSchema);
